'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var React = require('react');
var useIsBrowser = require('./use-is-browser.js');

/**
 * React hook for managing translate state via PointerEvent (handles both mouse and touch).
 * Consumer will need to add CSS `transform: translate(0px, 0px)` to the child element.
 *
 * NOTE: Only works in browsers where `window` is defined.
 *
 * https://developer.mozilla.org/en-US/docs/Web/API/PointerEvent
 *
 * @params UseTranslateXYOptions
 * @returns UseTranslateXYState
 */ const useTranslateXY = (options)=>{
    const { childRef , containerRef , enableKeyboardNudge =true , enabled =true  } = options;
    let { nudgeIncrement =50 , padding =200  } = options;
    padding = padding < 0 ? 0 : padding;
    nudgeIncrement = nudgeIncrement < 1 ? 1 : nudgeIncrement;
    const baseStart = {
        clientX: 0,
        clientY: 0,
        maxTranslateX: 0,
        maxTranslateY: 0,
        translateX: 0,
        translateY: 0
    };
    const baseTranslate = {
        x: 0,
        y: 0
    };
    const isBrowser = useIsBrowser.useIsBrowser();
    /**
   * Tracks the starting `pointerdown` coordinates of the container element
   * and starting `translate.x` and `translate.x`
   * Ref is needed when used inside event listeners
   */ const startRef = React.useRef(baseStart);
    /**
   * Tracks if translate x and y state. We need both ref and state
   * (ref is needed when used inside event listeners, state variable doesn't update there)
   */ const translateRef = React.useRef(baseTranslate);
    const [translate, setTranslate] = React.useState(baseTranslate);
    /**
   * Tracks if actively translating via PointerEvent on the child. We need both ref and state
   * (ref is needed when used inside event listeners, state variable doesn't update there)
   */ const translatingRef = React.useRef(false);
    const [translating, setTranslating] = React.useState(false);
    const pointerCancel = ()=>{
        translatingRef.current = false;
        setTranslating(false);
    };
    const reset = ()=>{
        startRef.current = baseStart;
        translateRef.current = baseTranslate;
        setTranslate(baseTranslate);
        pointerCancel();
    };
    React.useEffect(()=>{
        if (!enabled || !isBrowser) return;
        window.addEventListener('pointercancel', pointerCancel);
        window.addEventListener('pointerleave', pointerCancel);
        window.addEventListener('pointerup', pointerCancel);
        return ()=>{
            window.removeEventListener('pointercancel', pointerCancel);
            window.removeEventListener('pointerleave', pointerCancel);
            window.removeEventListener('pointerup', pointerCancel);
        };
    }, [
        enabled,
        isBrowser
    ]);
    const childPointerDown = (e)=>{
        if (e.button === 0 || e.pointerType !== 'mouse') {
            translatingRef.current = true;
            setTranslating(true);
        }
    };
    React.useEffect(()=>{
        const htmlElement = childRef?.current;
        if (!enabled || !htmlElement) return;
        htmlElement.addEventListener('pointerdown', childPointerDown);
        htmlElement.draggable = false;
        htmlElement.style.setProperty('-webkit-touch-callout', 'none');
        htmlElement.style.setProperty('-webkit-user-select', 'none');
        htmlElement.style.setProperty('user-select', 'none');
        return ()=>{
            htmlElement.removeEventListener('pointerdown', childPointerDown);
            htmlElement.removeAttribute('draggable');
            htmlElement.style.removeProperty('-webkit-touch-callout');
            htmlElement.style.removeProperty('-webkit-user-select');
            htmlElement.style.removeProperty('user-select');
        };
    }, [
        childRef,
        enabled
    ]); // eslint-disable-line react-hooks/exhaustive-deps
    const getMaximumTranslate = ()=>{
        if (childRef?.current && containerRef?.current) {
            const { height: childHeight , width: childWidth  } = childRef.current.getBoundingClientRect();
            const { height , width  } = containerRef.current.getBoundingClientRect();
            const maxTranslateX = width - Math.min(padding, childWidth) - (width - childWidth) / 2;
            const maxTranslateY = height - Math.min(padding, childHeight) - (height - childHeight) / 2;
            return {
                maxTranslateX,
                maxTranslateY
            };
        } else {
            return {
                maxTranslateX: 0,
                maxTranslateY: 0
            };
        }
    };
    const containerKeyDown = (e)=>{
        if (containerRef && document?.activeElement === containerRef.current && enableKeyboardNudge) {
            let changeX = 0;
            let changeY = 0;
            if (e.shiftKey) {
                switch(e.key){
                    case 'ArrowLeft':
                        {
                            const { maxTranslateX  } = getMaximumTranslate();
                            const { x  } = translateRef.current;
                            if (Math.abs(x - nudgeIncrement) <= maxTranslateX) {
                                changeX -= nudgeIncrement;
                            }
                            break;
                        }
                    case 'ArrowRight':
                        {
                            const { maxTranslateX  } = getMaximumTranslate();
                            const { x  } = translateRef.current;
                            if (Math.abs(x + nudgeIncrement) <= maxTranslateX) {
                                changeX += nudgeIncrement;
                            }
                            break;
                        }
                    case 'ArrowUp':
                        {
                            const { maxTranslateY  } = getMaximumTranslate();
                            const { y  } = translateRef.current;
                            if (Math.abs(y - nudgeIncrement) <= maxTranslateY) {
                                changeY -= nudgeIncrement;
                            }
                            break;
                        }
                    case 'ArrowDown':
                        {
                            const { maxTranslateY  } = getMaximumTranslate();
                            const { y  } = translateRef.current;
                            if (Math.abs(y + nudgeIncrement) <= maxTranslateY) {
                                changeY += nudgeIncrement;
                            }
                            break;
                        }
                }
            }
            if (changeX !== 0 || changeY !== 0) {
                setTranslate((prevTranslate)=>{
                    const x = prevTranslate.x + changeX;
                    const y = prevTranslate.y + changeY;
                    translateRef.current = {
                        x,
                        y
                    };
                    return {
                        x,
                        y
                    };
                });
            }
        }
    };
    const containerPointerDown = (e)=>{
        const { maxTranslateX , maxTranslateY  } = getMaximumTranslate();
        startRef.current = {
            clientX: e.clientX,
            clientY: e.clientY,
            maxTranslateX,
            maxTranslateY,
            translateX: translateRef.current.x,
            translateY: translateRef.current.y
        };
    };
    const containerPointerMove = (e)=>{
        if (translatingRef.current) {
            const { clientX , clientY , maxTranslateX , maxTranslateY , translateX , translateY  } = startRef.current;
            const changeX = e.clientX >= clientX ? e.clientX - clientX : -(clientX - e.clientX);
            const changeY = e.clientY >= clientY ? e.clientY - clientY : -(clientY - e.clientY);
            const nextX = translateX + changeX;
            const nextY = translateY + changeY;
            const newX = Math.abs(nextX) <= maxTranslateX ? nextX : translateRef.current.x;
            const newY = Math.abs(nextY) <= maxTranslateY ? nextY : translateRef.current.y;
            translateRef.current = {
                x: newX,
                y: newY
            };
            setTranslate({
                x: newX,
                y: newY
            });
        }
    };
    React.useEffect(()=>{
        const htmlElement = containerRef?.current;
        if (!enabled || !htmlElement) return;
        htmlElement.addEventListener('pointercancel', pointerCancel);
        htmlElement.addEventListener('pointerdown', containerPointerDown);
        htmlElement.addEventListener('pointermove', containerPointerMove);
        htmlElement.addEventListener('pointerup', pointerCancel);
        htmlElement.style.setProperty('touch-action', 'none');
        if (enableKeyboardNudge) {
            htmlElement.addEventListener('keydown', containerKeyDown);
            htmlElement.tabIndex = 0;
        }
        return ()=>{
            htmlElement.removeEventListener('pointercancel', pointerCancel);
            htmlElement.removeEventListener('pointerdown', containerPointerDown);
            htmlElement.removeEventListener('pointermove', containerPointerMove);
            htmlElement.removeEventListener('pointerup', pointerCancel);
            htmlElement.style.removeProperty('touch-action');
            if (enableKeyboardNudge) {
                htmlElement.removeEventListener('keydown', containerKeyDown);
                htmlElement.removeAttribute('tabindex');
            }
        };
    }, [
        containerRef,
        enableKeyboardNudge,
        enabled,
        nudgeIncrement,
        padding
    ]); // eslint-disable-line react-hooks/exhaustive-deps
    const manualTranslate = (translate)=>{
        translateRef.current = translate;
        setTranslate(translate);
    };
    return {
        reset,
        setTranslate: manualTranslate,
        translate,
        translated: translate.x !== 0 || translate.y !== 0,
        translating
    };
};

exports.useTranslateXY = useTranslateXY;
//# sourceMappingURL=use-translate-xy.js.map
